import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { KeycloakAuthGuard, KeycloakService } from 'keycloak-angular';

import { AuthService } from '../service/auth.service';
import { UserRole } from '@core/models/user.model';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard extends KeycloakAuthGuard {
  constructor(
    protected override router: Router,
    protected keycloakService: KeycloakService,
    private authService: AuthService
  ) {
    super(router, keycloakService);
  }

  async isAccessAllowed(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    // First check if user is authenticated
    if (!this.authenticated) {
      // Pass partner slug via login_hint so Keycloak login page can apply partner branding
      const slug = this.getPartnerSlug();
      await this.keycloakService.login({
        redirectUri: window.location.origin + state.url,
        ...(slug ? { loginHint: `partner:${slug}` } : {}),
      });
      return false;
    }

    // Debug: Log user roles
    const userRoles = this.keycloakService.getUserRoles();
    const keycloakInstance = this.keycloakService.getKeycloakInstance();
    const realmRoles = keycloakInstance?.tokenParsed?.realm_access?.roles || [];
    
    console.log('AuthGuard - User roles from getUserRoles():', userRoles);
    console.log('AuthGuard - Realm roles from token:', realmRoles);
    console.log('AuthGuard - Target URL:', state.url);

    // Check role-based access
    const requiredRoles = route.data['role'] as Array<UserRole>;
    const requiredKeycloakRoles = route.data['roles'] as string[];

    console.log('AuthGuard - Required Keycloak roles:', requiredKeycloakRoles);

    // If no roles are required, allow access
    if (!requiredRoles?.length && !requiredKeycloakRoles?.length) {
      return true;
    }

    // Check Keycloak roles (preferred method)
    if (requiredKeycloakRoles?.length) {
      // Check roles from multiple sources (getUserRoles may not include realm roles)
      const allUserRoles = [...new Set([...userRoles, ...realmRoles])];
      console.log('AuthGuard - All user roles:', allUserRoles);
      
      const hasRequiredRole = requiredKeycloakRoles.some(role => {
        const hasRole = allUserRoles.includes(role);
        console.log(`AuthGuard - Checking role '${role}': ${hasRole}`);
        return hasRole;
      });
      
      if (!hasRequiredRole) {
        // User doesn't have required role, redirect to unauthorized page
        console.log('AuthGuard - Access denied, redirecting to unauthorized');
        this.router.navigate(['/unauthorized']);
        return false;
      }
      return true;
    }

    // Fallback to UserRole-based check (legacy support)
    if (requiredRoles?.length) {
      const userRole = this.authService.currentUserValue?.role;
      
      if (userRole === undefined || requiredRoles.indexOf(userRole) === -1) {
        this.router.navigate(['/unauthorized']);
        return false;
      }
    }

    return true;
  }

  private getPartnerSlug(): string | undefined {
    const parts = window.location.hostname.split('.');
    const reserved = ['www', 'app', 'api', 'staging', 'dev', 'keycloak', 'localhost'];
    if (parts.length >= 2 && !reserved.includes(parts[0])) {
      return parts[0];
    }
    return undefined;
  }
}
